import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
// Round MAFIA-Country-1 — effective working-database country for labels.
import { getMafiaCountryCode } from "./mafiaIdentity";
import { MafiaPageHeader, TableSkeleton, EmptyState } from "./_shared";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import TablePager, { usePagedRows } from "./_shared/TablePager";
import MafiaTheme from "./MafiaTheme";


export default function ImportFromNJ({ onBack, onOpenEditPart }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [importing, setImporting] = useState(false);

  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const [selectedRowKey, setSelectedRowKey] = useState("");
  const [countryCode, setCountryCode] = useState("USA");
  const dialog = useAppDialog();

  useEffect(() => {
    mountedRef.current = true;

    async function initializePage() {
      try {
        // Round MAFIA-Country-1: the page's country now comes from the
        // EFFECTIVE working database (whoami: switcher pin → profile
        // claim) instead of a hardcoded "USA", so titles, tab labels and
        // the already-imported messages describe the right direction for
        // India users and for anyone who switched databases.
        setCountryCode(await getMafiaCountryCode());
        await refreshData(false);

        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      } catch (error) {
        console.error("Failed to initialize ImportFromNJ:", error);
        await dialog.warning("Failed to load import list.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }

    initializePage();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectedRow = useMemo(() => {
    return rows.find((row) => getRowKey(row) === selectedRowKey) || null;
  }, [rows, selectedRowKey]);

  const selectedPart = useMemo(() => {
    if (!selectedRow) {
      return { partNum: "", rev: 0, revMinor: 0 };
    }

    return {
      partNum: String(selectedRow.partNumber ?? ""),
      rev: Number(selectedRow.printRevision ?? 0),
      revMinor: Number(selectedRow.mafiaRevision ?? 0),
    };
  }, [selectedRow]);

  // Batch 3.2: global search removed; per-column filters (client-side over the
  // loaded NJ list) replace it, consistent with EPO / the other MAFIA tables.
  const [columnFilters, setColumnFilters] = useState({});
  const mafiaColumns = useMemo(() => [
    { id: "sel",           label: "Select",           type: "none" },
    { id: "partNumber",    label: "Part Number",      type: "text", get: (r) => r.partNumber || "" },
    { id: "printRevision", label: "Drawing Revision", type: "text", get: (r) => String(r.printRevision ?? "") },
    { id: "mafiaRevision", label: "MAFIA Revision",   type: "text", get: (r) => String(r.mafiaRevision ?? "") },
    { id: "createdBy",     label: "Created By",       type: "text", get: (r) => r.createdBy || "" },
    { id: "timeStamp",     label: "Time Stamp",       type: "none" },
    { id: "verified",      label: "Verified",         type: "boolean", get: (r) => r.verified },
    { id: "released",      label: "Released",         type: "boolean", get: (r) => r.released },
  ], []);
  const displayRows = useMemo(
    () => applyColumnFilters(rows, mafiaColumns, columnFilters),
    [rows, mafiaColumns, columnFilters]
  );

  // Round 60 — client-side pagination (the NJ parts list can be large).
  // Filters keep operating on the full displayRows; only the render pages.
  const pager = usePagedRows(
    displayRows,
    JSON.stringify(columnFilters)
  );

  async function refreshData(showSpinner = true) {
    try {
      if (showSpinner) {
        setRefreshing(true);
      }

      const data = await getListOfNjParts();
      if (!mountedRef.current) return;

      const safeRows = data || [];
      setRows(safeRows);

      if (selectedRowKey) {
        const stillExists = safeRows.some((row) => getRowKey(row) === selectedRowKey);
        if (!stillExists) {
          setSelectedRowKey("");
        }
      }
    } catch (error) {
      console.error("Failed to refresh import data:", error);
      await dialog.warning("Failed to load import list.");
    } finally {
      if (mountedRef.current) {
        setRefreshing(false);
      }
    }
  }

  function selectRow(row) {
    if (!row) return;
    setSelectedRowKey(getRowKey(row));
  }

  function openImportedEditPart(partData = selectedPart) {
    if (!partData.partNum) return;

    if (typeof onOpenEditPart === "function") {
      onOpenEditPart({
        partNum: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
        isImported: true,
        isNew: false,
      });
    } else {
      console.warn("Edit Part callback is not configured.");
    }
  }

  function handleSearchKeyDown(e) {
    if (e.key !== "Enter") return;

    if (displayRows.length > 0) {
      const firstMatch = displayRows[0];
      const rowKey = getRowKey(firstMatch);

      setSelectedRowKey(rowKey);

      const rowEl = rowRefs.current[rowKey];
      if (rowEl) {
        rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    if (searchInputRef.current) {
      searchInputRef.current.select();
    }
  }

  async function handleImportClick(partData = selectedPart) {
    if (!partData.partNum || importing) return;

    try {
      setImporting(true);

      const localTable = await getMafiaTable({
        partNumber: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
      });

      if ((localTable || []).length > 0) {
        if (countryCode === "USA") {
          await dialog.warning(
            "This part has already been imported from India and is already in the Florida MAFIA Central Database. Please use the 'Edit Part' button to edit already imported tables."
          );
        } else {
          await dialog.warning(
            "This part has already been imported from Florida and is already in the India MAFIA database. Please use the 'Edit Part' button to edit already imported tables."
          );
        }
        return;
      }

      const toCopy = await getMafiaTableSaturn({
        partNumber: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
      });

      if (!toCopy || toCopy.length === 0) {
        await dialog.error("Error in transferring table. Please try again or contact technical support.");
        return;
      }

      const copied = await copyToPluto(toCopy);
      if (!copied) {
        await dialog.error("Error in transferring table. Please try again or contact technical support.");
        return;
      }

      const initialized = await initializeIndiaTable({
        partNumber: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
      });

      if (!initialized) {
        await dialog.error("Error in initializing India MAFIA table. Please contact technical support.");
        return;
      }

      openImportedEditPart(partData);
    } catch (error) {
      console.error("Import failed:", error);
      // Round MAFIA-UAT-2 (2026) — surface the real backend message (the
      // copy endpoint returns 500-with-reason); the generic text alone made
      // this failure undiagnosable from the UI.
      const detail =
        (typeof error?.response?.data === "string" && error.response.data) ||
        error?.response?.data?.error ||
        error?.message ||
        "";
      await dialog.error(
        detail
          ? `Error in transferring table: ${detail}`
          : "Error in transferring table. Please try again or contact technical support."
      );
    } finally {
      if (mountedRef.current) {
        setImporting(false);
      }
    }
  }

  // Round MAFIA-UAT-1 (2026) — naming: for an India user the remote source
  // is FLORIDA (the "NJ" name predates the Florida move); matches the
  // renamed menu card.
  const title = countryCode === "USA" ? "Import from India" : "Import from Florida";
  const tabLabel = countryCode === "USA" ? "India Parts" : "Florida Parts";

  return (
    <div className="import-nj-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-download-cloud-2-line"
        eyebrow="MAFIA Aux"
        title={title}
        subtitle="Bring external part records into the local MAFIA Aux database."
        tone="blue"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: title },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="import-nj-toolbar-main">
                <div className="import-nj-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>

                <div className="import-nj-toolbar-main-right">
                  {/* Batch 3.2: global search removed — use the per-column filter
                      boxes under the table headers (consistent with EPO). */}
                  <span className="text-muted small">
                    <i className="ri-filter-3-line me-1" />
                    Filter using the column boxes below
                  </span>
                </div>
              </div>

              <div className="import-nj-toolbar-utility">
                <Button
                  color="primary"
                  className="import-nj-action-btn import-nj-utility-btn"
                  onClick={() => refreshData(true)}
                  disabled={refreshing || importing}
                >
                  <i className="ri-refresh-line import-nj-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh List"}
                </Button>

                <Button
                  color="success"
                  className="import-nj-action-btn import-nj-utility-btn"
                  onClick={() => handleImportClick()}
                  disabled={!selectedPart.partNum || importing}
                >
                  <i className="ri-download-cloud-2-line import-nj-btn-icon me-1" />
                  {importing ? "Importing..." : "Import Selected Part"}
                </Button>

                <Button
                  color="light"
                  className="import-nj-action-btn import-nj-utility-btn border"
                  onClick={() => openImportedEditPart()}
                  disabled={!selectedPart.partNum || importing}
                >
                  <i className="ri-edit-2-line import-nj-btn-icon me-1" />
                  Open Edit Part
                </Button>

                <Badge color="light" className="import-nj-queue-badge">
                  Queue Size: {displayRows.length}
                </Badge>

                <div className="import-nj-info-pill">
                  <span className="import-nj-info-label">Selected Part</span>
                  <span
                    className="import-nj-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="import-nj-info-pill">
                  <span className="import-nj-info-label">Revision</span>
                  <span className="import-nj-info-value">
                    {selectedRow
                      ? `${selectedRow.printRevision ?? 0}.${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="import-nj-refresh-note">
                  {importing ? "Import in progress" : `${title} • ${tabLabel}`}
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="import-nj-table-shell">
                <div className="import-nj-table-scroll">
                  <Table responsive={false} className="align-middle mb-0 import-nj-table">
                    <thead className="table-light">
                      <tr>
                        <th className="import-nj-col-select">Select</th>
                        <th>Part Number</th>
                        <th>Drawing Revision</th>
                        <th>MAFIA Revision</th>
                        <th>Created By</th>
                        <th>Time Stamp</th>
                        <th>Verified</th>
                        <th>Released</th>
                      </tr>
                      {/* Batch 3.2: per-column filter row (replaces global search). */}
                      <ColumnFilterRow
                        columns={mafiaColumns}
                        filters={columnFilters}
                        onChange={setColumnFilters}
                        live
                      />
                    </thead>
                    <tbody>
                      {loading ? (
                        <TableSkeleton rows={6} columns={8} />
                      ) : displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <EmptyState
                              icon="ri-download-cloud-2-line"
                              title="No parts available to import"
                              message={
                                Object.values(columnFilters || {}).some((v) => String(v || "").trim() !== "")
                                  ? "No parts match the current column filters. Try clearing them."
                                  : "No new parts found in the source database."
                              }
                              tone="secondary"
                            />
                          </td>
                        </tr>
                      ) : (
                        pager.pagedRows.map((row) => {
                          const rowKey = getRowKey(row);
                          const isSelected = rowKey === selectedRowKey;

                          return (
                            <tr
                              key={rowKey}
                              ref={(el) => {
                                if (el) {
                                  rowRefs.current[rowKey] = el;
                                }
                              }}
                              tabIndex={0}
                              onClick={() => selectRow(row)}
                              onDoubleClick={() =>
                                handleImportClick({
                                  partNum: row.partNumber,
                                  rev: row.printRevision,
                                  revMinor: row.mafiaRevision,
                                })
                              }
                              className={`import-nj-data-row ${isSelected ? "selected" : ""}`}
                            >
                              <td className="import-nj-col-select">
                                <Input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => selectRow(row)}
                                  name="selected-import-row"
                                />
                              </td>
                              <td><div className="fw-semibold text-dark">{row.partNumber ?? ""}</div></td>
                              <td>{row.printRevision ?? ""}</td>
                              <td>{row.mafiaRevision ?? ""}</td>
                              <td>{row.createdBy ?? ""}</td>
                              <td>{formatDateTime(row.timeStamp)}</td>
                              <td><StatusBadge value={row.verified} /></td>
                              <td><StatusBadge value={row.released} /></td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
                <TablePager {...pager} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .import-nj-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .import-nj-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .import-nj-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .import-nj-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .import-nj-search-group {
          width: 100%;
        }

        .import-nj-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .import-nj-action-btn:disabled,
          .import-nj-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .import-nj-utility-btn {
            min-width: 110px;
          }

        .import-nj-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .import-nj-queue-badge {
            display: inline-flex;
            align-items: center;
            min-height: 32px;
            border: 1px solid #e9ebec;
            padding: 0.3rem 0.65rem;
            border-radius: 0.375rem;
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            background: #f3f6f9;
          }

        .import-nj-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .import-nj-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .import-nj-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .import-nj-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #878a99;
          font-weight: 500;
        }

        .import-nj-table-shell {
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          background: #fff;
        }

        .import-nj-table-scroll {
          max-height: calc(100vh - 240px);
          min-height: 320px;
          overflow-x: auto;
          overflow-y: auto;
        }

        .import-nj-table {
          min-width: 1120px;
        }

        .import-nj-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f3f6f9;
          white-space: nowrap;
        }

        .import-nj-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #495057;
          vertical-align: middle;
          white-space: nowrap;
        }

        .import-nj-col-select {
          width: 72px;
          text-align: center;
        }

        .import-nj-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .import-nj-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .import-nj-data-row:hover {
          background: #f5f9ff;
        }

        .import-nj-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .import-nj-status {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.35rem 0.65rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 700;
        }

        .import-nj-status.yes {
          background: rgba(25, 135, 84, 0.12);
          color: #198754;
        }

        .import-nj-status.no {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }

        .import-nj-status.na {
          background: rgba(100, 116, 139, 0.12);
          color: #878a99;
        }

        @media (max-width: 1199.98px) {
          .import-nj-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .import-nj-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .import-nj-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .import-nj-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function getRowKey(row) {
  return `${row.partNumber ?? ""}-${row.printRevision ?? 0}-${row.mafiaRevision ?? 0}`;
}

function StatusBadge({ value }) {
  if (value === true) {
    return (
      <span className="import-nj-status yes">
        <i className="ri-checkbox-circle-fill" /> Yes
      </span>
    );
  }

  if (value === false) {
    return (
      <span className="import-nj-status no">
        <i className="ri-close-circle-fill" /> No
      </span>
    );
  }

  return <span className="import-nj-status na">—</span>;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

async function getListOfNjParts() {
  const response = await api.get("mafia/njparts");
  return (response.data || []).map((item) => ({
    partNumber: item.partNumber,
    printRevision: item.printRevision,
    mafiaRevision: item.mafiaRevision,
    createdBy: item.createdBy,
    timeStamp: item.timeStamp,
    verified: item.verified,
    released: item.released,
  }));
}

async function getMafiaTable(request) {
  const response = await api.post("mafia/mafiatable", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}

async function getMafiaTableSaturn(request) {
  const response = await api.post("mafia/mafiatable-saturn", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}

async function copyToPluto(rows) {
  const response = await api.post("mafia/copy-to-pluto", { rows });
  return response.data;
}

async function initializeIndiaTable(request) {
  const response = await api.post("mafia/initialize-india-table", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}